import {
  getPayrollRegister,
  getSalaryStatement,
  getTdsSummary,
  getPayrollSummary,
  getBankTransferReport,
} from "../payroll/payrollApi";
import { findEmployeeById, formatEmployeeLabel } from "../../components/common/employeeUtils";
import { employeeDepartmentId, departmentLabel } from "./employeeReportsUtils";

export { exportRowsToXlsx, toIsoDate } from "./employeeReportsUtils";
export { exportRowsToPdf } from "./attendanceReportsUtils";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  return `₹${num(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
}

function maskAccountNumber(acc) {
  const s = String(acc || "");
  if (s.length <= 4) return s;
  return `XXXX${s.slice(-4)}`;
}

/** Matches a component line by code or name against any of the given regexes. */
function lineMatches(line, patterns) {
  const hay = `${line.component_code || ""} ${line.name || ""}`.toLowerCase();
  return patterns.some((re) => re.test(hay));
}

const RE_BASIC = [/^basic/i, /\bbasic\b/i];
const RE_HRA = [/\bhra\b/i, /house rent/i];
const RE_PF = [/\bpf\b/i, /provident fund/i];
const RE_ESI = [/\besi\b/i, /state insurance/i];
const RE_PT = [/\bpt\b/i, /professional tax/i];
const RE_TDS = [/\btds\b/i, /tax deducted/i];

/**
 * Branch / employee-type scope chosen in the report filters: a Set of employee_id
 * strings, or null when no such filter is active (everyone is in scope).
 */
export function inScope(scopeIds, employeeId) {
  return !scopeIds || scopeIds.has(String(employeeId));
}

function departmentMatches(employee, departmentId) {
  if (!departmentId) return true;
  return employeeDepartmentId(employee) === String(departmentId);
}

/** 1. Payroll Register — Employee, Gross, Deductions, Net. */
export async function buildPayrollRegisterRows({ runId, departmentId, employees, scopeIds = null }) {
  if (!runId) return [];
  const res = await getPayrollRegister(runId, { live: false });
  const rows = (res.data?.register || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows
    .map((r) => {
      const emp = findEmployeeById(employees, r.employee_id);
      return { emp, r };
    })
    .filter(({ emp }) => departmentMatches(emp, departmentId))
    .map(({ emp, r }) => ({
      Employee: formatEmployeeLabel(emp) || "Unknown employee",
      Department: departmentLabel(emp),
      Gross: money(r.earnings),
      Deductions: money(r.deductions),
      Net: money(r.net_salary),
    }));
}

/** 2. Salary Register — Basic, HRA, Allowances, PF, ESI, PT, Net Salary. */
export async function buildSalaryRegisterRows({ runId, departmentId, employees, scopeIds = null }) {
  if (!runId) return [];
  const res = await getSalaryStatement(runId);
  const rows = (res.data?.salary_statement || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows
    .map((r) => {
      const emp = findEmployeeById(employees, r.employee_id);
      return { emp, r };
    })
    .filter(({ emp }) => departmentMatches(emp, departmentId))
    .map(({ emp, r }) => {
      const lines = r.component_lines || [];
      const basic = lines.filter((l) => lineMatches(l, RE_BASIC));
      const hra = lines.filter((l) => lineMatches(l, RE_HRA));
      const pf = lines.filter((l) => lineMatches(l, RE_PF) && l.category !== "employer_contribution");
      const esi = lines.filter((l) => lineMatches(l, RE_ESI) && l.category !== "employer_contribution");
      const pt = lines.filter((l) => lineMatches(l, RE_PT));
      const tds = lines.filter((l) => lineMatches(l, RE_TDS));
      const excluded = new Set([...basic, ...hra].map((l) => l.component_code));
      const allowances = lines.filter(
        (l) => l.category === "earning" && !excluded.has(l.component_code)
      );
      const sum = (arr) => arr.reduce((acc, l) => acc + num(l.amount), 0);

      return {
        Employee: formatEmployeeLabel(emp) || r.employee_name || "Unknown employee",
        Department: departmentLabel(emp),
        Basic: money(sum(basic)),
        HRA: money(sum(hra)),
        Allowances: money(sum(allowances)),
        PF: money(sum(pf)),
        ESI: money(sum(esi)),
        PT: money(sum(pt)),
        TDS: money(sum(tds)),
        "Net Salary": money(r.total_amount),
      };
    });
}

/** 3. Bank Transfer Report — Employee, Bank Account, Net Salary. */
export async function buildBankTransferRows({ runId, employees, mask = true, scopeIds = null }) {
  if (!runId) return [];
  const res = await getBankTransferReport(runId);
  const rows = (res.data?.bank_transfer_report || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows.map((r) => {
    const emp = findEmployeeById(employees, r.employee_id);
    const account = r.account_number
      ? `${r.bank_name || "Bank"} • ${mask ? maskAccountNumber(r.account_number) : r.account_number}`
      : "No bank details on file";
    return {
      Employee: formatEmployeeLabel(emp) || r.employee_name || "Unknown employee",
      "Bank Account": account,
      "IFSC Code": r.ifsc_code || "—",
      "Net Salary": money(r.net_salary),
    };
  });
}

/** 4. LOP (Loss of Pay) Report — Employee, LOP Days, LOP Amount. */
export async function buildLopReportRows({ runId, departmentId, employees, scopeIds = null }) {
  if (!runId) return [];
  const res = await getPayrollRegister(runId, { live: true });
  const rows = (res.data?.register || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows
    .map((r) => {
      const emp = findEmployeeById(employees, r.employee_id);
      return { emp, r };
    })
    .filter(({ emp }) => departmentMatches(emp, departmentId))
    .filter(({ r }) => num(r.lop_units) > 0)
    .map(({ emp, r }) => ({
      Employee: formatEmployeeLabel(emp) || "Unknown employee",
      Department: departmentLabel(emp),
      "LOP Days": num(r.lop_units).toFixed(num(r.lop_units) % 1 ? 1 : 0),
      "LOP Amount": r.lop_amount != null ? money(r.lop_amount) : "—",
    }));
}

/** Shared PF/ESI builder — Employee, Employee Contribution, Employer Contribution. */
async function buildStatutorySplitRows({ runId, employees, patterns, scopeIds = null }) {
  if (!runId) return [];
  const res = await getSalaryStatement(runId);
  const rows = (res.data?.salary_statement || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows
    .map((r) => {
      const emp = findEmployeeById(employees, r.employee_id);
      const lines = (r.component_lines || []).filter((l) => lineMatches(l, patterns));
      const employeeAmt = lines
        .filter((l) => l.category !== "employer_contribution")
        .reduce((acc, l) => acc + num(l.amount), 0);
      const employerAmt = lines
        .filter((l) => l.category === "employer_contribution")
        .reduce((acc, l) => acc + num(l.amount), 0);
      return { emp, r, lines, employeeAmt, employerAmt };
    })
    .filter(({ lines }) => lines.length > 0)
    .map(({ emp, r, employeeAmt, employerAmt }) => ({
      Employee: formatEmployeeLabel(emp) || r.employee_name || "Unknown employee",
      "Employee Contribution": money(employeeAmt),
      "Employer Contribution": money(employerAmt),
    }));
}

/** 5. PF Report — Employee PF, Employer PF. */
export function buildPfReportRows({ runId, employees, scopeIds = null }) {
  return buildStatutorySplitRows({ runId, employees, patterns: RE_PF, scopeIds });
}

/** 6. ESI Report — Employee ESI, Employer ESI. */
export function buildEsiReportRows({ runId, employees, scopeIds = null }) {
  return buildStatutorySplitRows({ runId, employees, patterns: RE_ESI, scopeIds });
}

/** 7. TDS Report — Employee, Taxable Income, TDS Deducted. */
export async function buildTdsReportRows({ runId, employees, scopeIds = null }) {
  if (!runId) return [];
  const res = await getTdsSummary(runId);
  const rows = (res.data?.tds_summary || []).filter((r) => inScope(scopeIds, r.employee_id));
  return rows.map((r) => {
    const emp = findEmployeeById(employees, r.employee_id);
    return {
      Employee: formatEmployeeLabel(emp) || "Unknown employee",
      "Taxable Income": money(r.taxable_salary),
      "TDS Deducted": money(r.tds),
    };
  });
}

/** 8. Payroll Summary Report — single aggregate row.
 * Note: getPayrollSummary's "entries" is a count of PayrollEntry ledger lines
 * (one per component per employee), not a distinct-employee count — so the
 * employee headcount is taken from the register endpoint instead. */
export async function buildPayrollSummaryRow({ runId, scopeIds = null }) {
  if (!runId) return [];
  const [summaryRes, registerRes] = await Promise.all([
    getPayrollSummary(runId),
    getPayrollRegister(runId, { live: false }),
  ]);
  const registerRows = (registerRes.data?.register || []).filter((r) => inScope(scopeIds, r.employee_id));
  // With a branch / employee-type filter the run-wide totals no longer apply,
  // so they are summed from the filtered register rows instead.
  const totals = scopeIds
    ? registerRows.reduce(
        (acc, r) => ({
          earnings: acc.earnings + num(r.earnings),
          deductions: acc.deductions + num(r.deductions),
          net: acc.net + num(r.net_salary),
        }),
        { earnings: 0, deductions: 0, net: 0 }
      )
    : summaryRes.data?.totals || {};
  return [
    {
      "Total Employees": registerRows.length,
      "Gross Salary": money(totals.earnings),
      "Total Deductions": money(totals.deductions),
      "Net Salary": money(totals.net),
    },
  ];
}
